import type { ExtractedTableData } from '@neo/blocks/data-table/types'

// Pagination `meta` on the v1 list endpoints: `total_count` is absent on Call/Recording.
export interface ListPaginationMeta {
  total_count?: number
  offset?: number
  next?: string | null
  previous?: string | null
}

// RemoteTable pagination from an offset-paged `meta`: exact total when present, else "of many".
export const extractPaginationFromMeta = (
  meta?: ListPaginationMeta,
): Pick<ExtractedTableData<unknown>, 'totalCount' | 'hasNext' | 'hasPrevious'> => ({
  totalCount: meta?.total_count ?? undefined,
  hasNext: !!meta?.next,
  hasPrevious: !!meta?.previous,
})

// Cursor token from a next/previous link (relative or absolute), percent-decoded.
const cursorOf = (link?: string | null) =>
  link ? (new URL(link, 'http://x').searchParams.get('cursor') ?? undefined) : undefined

// Call/Recording only: also follow the `cursor=` links and echo the offset the server applied.
export const extractCursorPagination = (
  meta?: ListPaginationMeta,
): Omit<ExtractedTableData<unknown>, 'data'> => ({
  ...extractPaginationFromMeta(meta),
  nextCursor: cursorOf(meta?.next),
  previousCursor: cursorOf(meta?.previous),
  pageOffset: meta?.offset,
})

/**
 * Clamp a page-size param to `[1, cap]`, falling back to `cap` when it isn't a number.
 *
 * Several v1 list endpoints (Recording, Transcription) silently cap `limit` server-side:
 * asking for 100 returns `cap` rows with no error, which would leave the table believing
 * it requested a larger page than it received. Clamping before the request keeps the
 * requested and returned page sizes in agreement.
 */
export const clampPageSize = (value: unknown, cap: number): number => {
  const parsed = parseInt(String(value), 10)
  return Number.isNaN(parsed) ? cap : Math.min(Math.max(parsed, 1), cap)
}
